import * as React from "react"
import { DndProvider } from "react-dnd"
import { HTML5Backend } from "react-dnd-html5-backend"
import { useNavigate, useParams } from "react-router-dom"

import { useQuery } from "@tanstack/react-query"
import { AxiosError } from "axios"

import copySoundboard from "@podcastsoundboard/ps-lib/api/soundboards/copy"
import getPublicSoundboard from "@podcastsoundboard/ps-lib/api/soundboards/getPublicSoundboard"
import PageContainer from "@podcastsoundboard/ps-lib/components/PageContainer/PageContainer"
import FrontendWebSoundboard from "@podcastsoundboard/ps-lib/types/FrontendWebSoundboard"
import KeyboardToggle from "@podcastsoundboard/ps-lib/components/KeyboardToggle"

import AppBar from "../../components/AppBar"
import Copyright from "../../components/Copyright"
import HeadingContainer from "../../components/HeadingContainer"
import audioStack from "../../lib/audioStack"
import isSafari from "../../lib/isSafari"
import MIDIDevicePicker from "../../pages/Soundboards/components/MIDIDevicePicker"
import SoundboardComponent from "../../pages/Soundboards/components/Soundboard"
import { useAppDispatch, useAppSelector } from "../../redux"
import { setIsDucked } from "../../redux/isDucked"
import { addSnackbar } from "../../redux/snackbars"
import SoundboardTopBarButton from "@podcastsoundboard/ps-lib/components/SoundboardTopBarButton"
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Link,
  LoadingButton,
  Paper,
  Stack,
  Toolbar,
  Tooltip,
  Typography,
} from "@podcastsoundboard/ps-lib/ui"
import KeyboardDoubleArrowDownIcon from "@podcastsoundboard/ps-lib/ui/icons/KeyboardDoubleArrowDown"
import LooksOneIcon from "@podcastsoundboard/ps-lib/ui/icons/LooksOne"
import PauseCircleIcon from "@podcastsoundboard/ps-lib/ui/icons/PauseCircle"

// const SoundboardComponent = React.lazy(
//   () => import("../../pages/Soundboards/components/Soundboard")
// )

export default function ShowPublicSoundboard(): React.ReactElement {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const currentUserProfile = useAppSelector((state) => state.currentUserProfile)
  const darkModeEnabled = useAppSelector((state) => state.darkModeEnabled)
  const { soundboardUuid } = useParams<{ soundboardUuid?: string }>()
  const isDucked = useAppSelector((state) => state.isDucked)
  const [open, setOpen] = React.useState(false)
  const [copying, setCopying] = React.useState(false)

  const {
    data: soundboard,
    error: fetchError,
    isFetching: soundboardLoading,
  } = useQuery<FrontendWebSoundboard, AxiosError>({
    queryKey: ["currentUserProfile", soundboardUuid],
    queryFn: async ({ queryKey }) => {
      return getPublicSoundboard(queryKey[1] as string)
    },
  })

  const [errorMessage, setErrorMessage] = React.useState("")

  React.useEffect(() => {
    if (fetchError) {
      setErrorMessage(fetchError?.message || "")
    }
  }, [fetchError])

  const handleImportClick = React.useCallback(async () => {
    if (!soundboardUuid) return
    try {
      setCopying(true)
      const newSoundboard = await copySoundboard({ soundboardUuid })
      setCopying(false)
      navigate(`/app/account/soundboards/${newSoundboard.uuid}`)
    } catch (err) {
      setCopying(false)
      console.error("Error copying soundboard", err)
      dispatch(addSnackbar({ text: "Error copying soundboard" }))
    }
  }, [dispatch, navigate, soundboardUuid])

  return (
    <PageContainer darkModeEnabled={darkModeEnabled} className="min-h-screen">
      <Dialog
        open={open}
        onClose={() => !copying && setOpen(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle id="alert-dialog-title">
          {currentUserProfile ? "Import soundboard" : "Sign up to edit"}
        </DialogTitle>

        <DialogContent>
          <Typography variant="body1">
            {currentUserProfile ? (
              <>
                Before customizing this soundboard, first hit "Import" below to
                create a copy of this soundboard in your account. All sound
                files will be copied into your account and your own editable
                copy of this soundboard will be created.
              </>
            ) : (
              <>You need an account to edit this soundboard.</>
            )}
          </Typography>
        </DialogContent>

        <DialogActions>
          {currentUserProfile ? (
            <LoadingButton
              onClick={() => {
                handleImportClick()
              }}
              loading={copying}
              variant="contained"
            >
              Import
            </LoadingButton>
          ) : (
            <LoadingButton
              onClick={() => {
                navigate("/app/signup")
              }}
              variant="contained"
            >
              Sign Up
            </LoadingButton>
          )}
          <LoadingButton onClick={() => setOpen(false)} disabled={copying}>
            Cancel
          </LoadingButton>
        </DialogActions>
      </Dialog>

      <AppBar title={"Podcast Soundboard"} />

      <Toolbar />

      {errorMessage && (
        <Stack className="w-full" spacing={2} flexShrink={1} flexGrow={0}>
          <Alert severity="error" className="rounded-none">
            {errorMessage}
          </Alert>
        </Stack>
      )}

      {soundboard && (
        <>
          <Stack className="w-full" spacing={2} flexGrow={1}>
            <Alert severity="info" icon={false} className="rounded-none">
              <Stack direction="row" alignItems="center" spacing={2}>
                <Button
                  variant="contained"
                  color="info"
                  size="small"
                  onClick={() => setOpen(true)}
                >
                  Import
                </Button>
                <span>
                  This is a publicly shared soundboard. Import this soundboard
                  into your account to customize it.
                </span>
              </Stack>
            </Alert>
          </Stack>

          <Paper
            className="p-4 px-6"
            style={{ boxShadow: "none", border: "none" }}
          >
            <Stack direction="column" alignItems="stretch" spacing={1}>
              <Typography variant="h6">{soundboard?.name || "..."}</Typography>
              {soundboard?.description && (
                <Typography
                  variant="body1"
                  color="text.secondary"
                  style={{ whiteSpace: "pre" }}
                >
                  {soundboard?.description || ""}
                </Typography>
              )}
            </Stack>
          </Paper>

          <HeadingContainer minHeight="84px">
            <Stack
              display="flex"
              flexDirection="row"
              alignItems="center"
              flexWrap="wrap"
              width="100%"
              gap="0.5rem"
              className="pt-2 pb-2"
            >
              {soundboard && (
                <SoundboardTopBarButton
                  tooltipTitle="Pause all sounds"
                  label="Pause"
                  onClick={() => audioStack.pauseAll()}
                  icon={<PauseCircleIcon />}
                />
              )}

              {soundboard && soundboard.solo && (
                <Box>
                  <Tooltip title="Solo activated">
                    <IconButton aria-label="Solo activated" className="my-0">
                      <LooksOneIcon className="text-primary" />
                    </IconButton>
                  </Tooltip>
                </Box>
              )}

              {soundboard && (
                <Box>
                  <Tooltip title={isDucked ? "Audio reduced" : "Duck audio"}>
                    <IconButton
                      className="my-0"
                      aria-label="Duck"
                      onClick={() => dispatch(setIsDucked(!isDucked))}
                    >
                      <KeyboardDoubleArrowDownIcon
                        className={isDucked ? "text-primary" : undefined}
                      />
                    </IconButton>
                  </Tooltip>
                </Box>
              )}

              <Box>
                <KeyboardToggle />
              </Box>

              {!isSafari() && (
                <Box width="100%" maxWidth="200px">
                  <MIDIDevicePicker />
                </Box>
              )}

              {soundboardLoading && (
                <Box width="100%" maxWidth="40px">
                  <CircularProgress size={30} />
                </Box>
              )}
            </Stack>
          </HeadingContainer>

          <Divider />

          <Grid
            container
            spacing={0.375}
            className="px-4"
            style={{ position: "relative", marginTop: 0 }}
          >
            {soundboard && !soundboardLoading && (
              <DndProvider backend={HTML5Backend}>
                <SoundboardComponent
                  trialMode={false}
                  publicMode
                  soundboard={soundboard}
                  overrideOnCogPress={() => setOpen(true)}
                />
              </DndProvider>
            )}
          </Grid>
        </>
      )}

      {!soundboard && soundboardLoading && (
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="center"
          width="100%"
          className="p-4"
        >
          <CircularProgress size={30} />
        </Stack>
      )}

      {!soundboard && !soundboardLoading && (
        <Container maxWidth="md" className="py-10">
          <Paper className="p-4">
            <Typography variant="h6" className="mb-4">
              Soundboard not found
            </Typography>
            <Typography variant="body1">
              The soundboard you are looking for could not be found.{" "}
              <Link
                href="/app/account/soundboards/new"
                onClick={(e) => {
                  e.preventDefault()
                  navigate("/app/account/soundboards/new")
                }}
              >
                Create your own
              </Link>
              .
            </Typography>
          </Paper>
        </Container>
      )}

      <Copyright className="pt-8" />
    </PageContainer>
  )
}
